import {
  Button,
  ButtonGroup,
  Chip,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Selection,
} from '@heroui/react';
import React from 'react';
import { FiUsers } from 'react-icons/fi';
import { MdPets } from 'react-icons/md';

import { usePuppyMode, PuppyFilterMode } from '../../contexts/PuppyModeContext';

export interface PuppyModeToggleProps {
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Size variant for the toggle buttons
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Whether to show as a compact dropdown instead of full buttons
   * @default true
   */
  compact?: boolean;
}

/**
 * PuppyModeToggle - Toggle component for switching between normal and puppy users
 *
 * This component provides a toggle interface for filtering between normal users
 * and puppy/test users. It's only visible in non-production environments
 * for safety and testing purposes.
 *
 * Features:
 * - Two modes: Normal Users (none puppy), Puppy Users
 * - Only visible in non-production environments
 * - Visual indicators with emojis
 * - Compact chip variant for smaller spaces
 * - Integration with usePuppyMode hook for state management
 */
export const PuppyModeToggle: React.FC<PuppyModeToggleProps> = ({
  className = '',
  size = 'sm',
  compact = true,
}) => {
  const { isPuppyModeAvailable, filterMode, setFilter, environment } =
    usePuppyMode();

  // Don't render in production environments
  if (!isPuppyModeAvailable) {
    return null;
  }

  const getModeIcon = (mode: PuppyFilterMode) => {
    switch (mode) {
      case 'normal':
        return <FiUsers className="w-4 h-4" />;
      case 'puppy':
        return <MdPets className="w-4 h-4" />;
      default:
        return <FiUsers className="w-4 h-4" />;
    }
  };

  if (compact) {
    const handleSelectionChange = (keys: Selection) => {
      const selectedKey = Array.from(keys)[0] as string;
      if (selectedKey) {
        // Only allow 'normal' or 'puppy', convert 'all' to 'normal'
        const selectedMode: PuppyFilterMode =
          selectedKey === 'puppy' ? 'puppy' : 'normal';
        setFilter(selectedMode);
      }
    };

    const getFilterIcon = () => {
      // Use the mode-specific icon with a filter overlay concept
      // If 'all' is selected, show 'normal' icon (since we're removing 'all' option)
      const displayMode = filterMode === 'all' ? 'normal' : filterMode;
      return getModeIcon(displayMode);
    };

    const getIconButtonColor = () => {
      if (filterMode === 'puppy')
        return 'text-warning-600 dark:text-warning-400';
      return 'text-gray-700 dark:text-gray-300';
    };

    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Dropdown>
          <DropdownTrigger>
            <Button
              isIconOnly
              variant="light"
              className="hover:bg-gray-100 dark:hover:bg-gray-800 transition-all border border-transparent hover:border-gray-200 dark:hover:border-gray-600"
              size="lg"
              aria-label={`Filter users: ${filterMode}`}
            >
              <div className={`${getIconButtonColor()} relative`}>
                {getFilterIcon()}
              </div>
            </Button>
          </DropdownTrigger>
          <DropdownMenu
            aria-label="Puppy mode filter options"
            selectionMode="single"
            selectedKeys={
              new Set([filterMode === 'all' ? 'normal' : filterMode])
            }
            onSelectionChange={handleSelectionChange}
            className="min-w-48"
          >
            <DropdownItem
              key="normal"
              className="p-2"
              startContent={getModeIcon('normal')}
            >
              Normal Users (None Puppy)
            </DropdownItem>
            <DropdownItem
              key="puppy"
              className="p-2"
              startContent={getModeIcon('puppy')}
            >
              Puppy Users
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <span className="text-sm text-default-500 font-medium">Filter:</span>
      <ButtonGroup size={size} isDisabled={false}>
        <Button
          variant={
            filterMode === 'normal' || filterMode === 'all'
              ? 'solid'
              : 'bordered'
          }
          color={
            filterMode === 'normal' || filterMode === 'all'
              ? 'primary'
              : 'default'
          }
          onPress={() => setFilter('normal')}
          className="min-w-0"
          startContent={getModeIcon('normal')}
        >
          Normal Users (None Puppy)
        </Button>
        <Button
          variant={filterMode === 'puppy' ? 'solid' : 'bordered'}
          color={filterMode === 'puppy' ? 'warning' : 'default'}
          onPress={() => setFilter('puppy')}
          className="min-w-0"
          startContent={getModeIcon('puppy')}
        >
          Puppy Users
        </Button>
      </ButtonGroup>

      {/* Environment indicator */}
      <Chip
        color="warning"
        variant="flat"
        size="sm"
        className="text-xs"
        title={`${environment.name} Environment`}
      >
        {environment.emoji} {environment.name}
      </Chip>
    </div>
  );
};
